import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { format, parse } from "date-fns";

/**
 * Structural shape of one employment entry — matches `ProspectEmploymentsSchema`
 * (get-people.ts) without importing it, so the tracker and the extension can
 * feed the same list from their own prospect types.
 */
export interface WorkHistoryEmployment {
	title?: string | null;
	organizationName?: string | null;
	startedAt?: string | null;
	endedAt?: string | null;
	current?: boolean | null;
	linkedinUrl?: string | null;
}

const toMonthYear = (date?: string | null): string => {
	if (!date) {
		return "";
	}

	try {
		return format(parse(date, "yyyy-MM-dd", new Date()), "MMM yyyy");
	} catch {
		return "";
	}
};

/**
 * The "Work history" section rendered inside the expanded person identity card
 * (See work history). Current employment gets a highlighted timeline edge.
 */
export const WorkHistoryList: FunctionComponent<{
	employments: WorkHistoryEmployment[];
}> = ({ employments }) => {
	const { t } = useTranslation();

	if (employments.length === 0) {
		return null;
	}

	return (
		<div className="flex flex-col gap-1.5">
			<div className="text-[10px] font-bold uppercase tracking-wider text-text-tertiary">
				{t("contacts.leadPanel.identity.workHistory")}
			</div>
			<div className="flex max-h-[400px] flex-col gap-2.5 overflow-y-auto">
				{employments.map((employment, index) => {
					const startDate = toMonthYear(employment.startedAt);
					const endDate = employment.endedAt
						? toMonthYear(employment.endedAt)
						: t("contacts.leadPanel.identity.present");

					return (
						<div
							key={`${employment.organizationName}-${index}`}
							className={`relative flex flex-col border-l-2 py-0.5 pl-2.5 ${
								employment.current ? "border-status-success" : "border-gray-200"
							}`}
						>
							{employment.current && (
								<span className="absolute -left-[5px] top-0 h-2 w-2 rounded-full border-2 border-white bg-status-success" />
							)}
							{employment.title && (
								<div className="text-xs font-semibold text-text">{employment.title}</div>
							)}
							{employment.organizationName && (
								<div className="text-xs font-medium text-text-secondary">
									{employment.linkedinUrl ? (
										<a
											className="hover:underline"
											href={employment.linkedinUrl}
											referrerPolicy="no-referrer"
											rel="noreferrer"
											target="_blank"
										>
											{employment.organizationName}
										</a>
									) : (
										employment.organizationName
									)}
								</div>
							)}
							{startDate && (
								<div className="text-xxs font-medium text-text-tertiary">
									{startDate} – {endDate}
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};
